import { describeTestResultError } from 'src/hooks/useStructuredTestResults';
import { translate } from 'src/hooks/useTranslate';
import { english } from 'src/i18n/language';

// What the Test modal says about the lab report inbox (TGCLOUD-1866 Phase 3), in one place so the
// panel, the form and their tests use the same words. User-facing, the inbox is the "Lab report
// inbox". A match is a person's decision in the inbox, never a score: nothing here speaks of
// likelihood.
//
// TRANSLATION (TGCLOUD-1893). A refusal's `title` stays English and is translated where it is
// shown (LabReportApply): it is always one of the titles below. Its `message` may be the server's
// own words, which are never translated, so a message of ours is translated here, when the refusal
// is described. sentToInboxLines takes the caller's `t`.

// A refusal of the hand-off (POST /test-results/inbox/:id/intake), headed by what happened. The
// server's own message follows it (backend services/testResults/inboxWorkflow.js wrote each one,
// with what to do next), or the view's own words for a refusal it knows by its code
// (useStructuredTestResults REFUSAL_TEXT).
//
// ONE PERSON OF A REPORT ABOUT SEVERAL (TGCLOUD-1868, Phase E4). The hand-off reads one person's part
// of a trio's or a prenatal report (the link's `individual`), and is refused, before anything is
// read into the Test, where that person is matched to another fetus record, is not on the report,
// is not named, or does not fit the Test.
//
// EACH REASON BESIDE A CODE HAS A TITLE OF ITS OWN (TGCLOUD-1868, round 5: every refusal code gets
// its own wording). INBOX_ITEM_NOT_MATCHED and INDIVIDUAL_NOT_FOR_THIS_TEST each cover cases the
// server tells apart by a `reason` (backend rules.REFUSAL_REASONS): each is headed here by what
// happened, as `CODE:reason`. INBOX_ITEM_NOT_MATCHED is a code TGCLOUD-1866 shipped, so its sentence
// stays the server's, which says exactly where the report is; INDIVIDUAL_NOT_FOR_THIS_TEST's is the
// view's (useStructuredTestResults). labReportRefusalVocabulary.test.js fails on a reason the server
// adds with no title here.
export const HAND_OFF_TITLES = Object.freeze({
  INBOX_ITEM_NOT_MATCHED: 'Not matched in the Lab Report Inbox',
  'INBOX_ITEM_NOT_MATCHED:token': 'Not a reading of this inbox report',
  'INBOX_ITEM_NOT_MATCHED:item': 'Not in this group’s Lab Report Inbox',
  'INBOX_ITEM_NOT_MATCHED:report': 'A different report in the Lab Report Inbox',
  'INBOX_ITEM_NOT_MATCHED:individuals':
    'The Lab Report Inbox reads its people differently',
  'INBOX_ITEM_NOT_MATCHED:individual':
    'Not a person the Lab Report Inbox holds on this report',
  'INBOX_ITEM_NOT_MATCHED:state': 'Not matched in the Lab Report Inbox',
  'INBOX_ITEM_NOT_MATCHED:set-aside': 'Set aside in the Lab Report Inbox',
  'INBOX_ITEM_NOT_MATCHED:person': 'Matched to someone else',
  'INBOX_ITEM_NOT_MATCHED:fetus': 'Matched to another fetus record',
  'INBOX_ITEM_NOT_MATCHED:rejected': 'Rejected in the Lab Report Inbox',
  'INBOX_ITEM_NOT_MATCHED:conflict': 'The report and this record disagree',
  'INBOX_ITEM_NOT_MATCHED:ambiguous':
    'Another patient matches this report equally',
  'INBOX_ITEM_NOT_MATCHED:weak-needs-inbox':
    'A weak match, to confirm in the Lab Report Inbox',
  'INBOX_ITEM_NOT_MATCHED:same-person':
    'Already matched as another person on this report',
  // A fetus record the inbox cannot match the report's fetus to (TGCLOUD-1868 closeout review).
  'INBOX_ITEM_NOT_MATCHED:not-a-candidate':
    'Not a fetus record the Lab Report Inbox matches it to',
  INDIVIDUAL_NOT_FOR_THIS_TEST: 'Not a Test for this person on the report',
  'INDIVIDUAL_NOT_FOR_THIS_TEST:fetus-result': 'Not a Test for this fetus',
  'INDIVIDUAL_NOT_FOR_THIS_TEST:not-a-fetus-result': 'A Test about a pregnancy',
  'INDIVIDUAL_NOT_FOR_THIS_TEST:role-undetermined':
    'Not known to be a fetus or a born child',
  'INDIVIDUAL_NOT_FOR_THIS_TEST:another-individual':
    'Read for another person on the report',
  // A report about one fetus, handed to a Test that is not that fetus's (TGCLOUD-1868).
  'INDIVIDUAL_NOT_FOR_THIS_TEST:single-fetus-report':
    'Not a Test for the fetus on this report',
  INBOX_INDIVIDUAL_REQUIRED:
    'The report gives results for more than one person',
  INBOX_INDIVIDUAL_NOT_FOUND: 'Not a person on this report',
  INBOX_NO_INDIVIDUALS: 'The report is about one person',
  INBOX_INDIVIDUALS_CHANGED: 'The report’s people are read differently now',
  REPORT_PEOPLE_NOT_SPLIT: 'Its results could not be told apart by person',
  // Round 4: the family holds the report whole, from before each person's result was read on its own.
  REPORT_APPLIED_WHOLE: 'Already applied whole to another Test',
  // Migration 0052 not run on this installation (a customer-hosted one).
  REPORT_INDIVIDUAL_INDEX_MISSING: 'The database needs an update first',
  INBOX_EVIDENCE_CHANGED: 'The report has changed since it was matched',
  MATCH_AMBIGUOUS: 'Another patient now matches this report',
  MATCH_CONFLICT: 'The report and this record disagree',
  REPORT_ALREADY_APPLIED: 'Already applied to another Test',
  SOURCE_DOCUMENT_CHANGED: 'The stored report could not be read back',
  SOURCE_DOCUMENT_KEY_MISMATCH: 'The stored report could not be read back',
  INBOX_STATE_CHANGED: 'Changed in the Lab Report Inbox meanwhile',
  INBOX_NOT_FOUND: 'No Lab Report Inbox here',
  INBOX_ITEM_NOT_FOUND: 'Not in the Lab Report Inbox'
});

const NO_INBOX = 'You do not have access to the Lab Report Inbox.';

/**
 * A failed hand-off, for the review panel: `{ title, message, reasons, code, status }`.
 * The module gate's bare 403 ("Forbidden", a security group that does not grant the inbox) is
 * said in words. `context.individual` is the person of the report the hand-off was for, when it
 * was for one (describeTestResultError).
 */
export function describeInboxHandOffError(err, context = {}) {
  const described = describeTestResultError(
    err,
    translate('The report from the Lab Report Inbox could not be read.'),
    context
  );
  const data = err?.response?.data || {};
  const forbidden =
    described.status === 403 &&
    !data.code &&
    (data.message === 'Forbidden' || !data.message);
  // A refusal with a reason is headed for that reason where there are words for it, else for its
  // code.
  const key =
    data.code && typeof data.reason === 'string' && data.reason
      ? `${data.code}:${data.reason}`
      : data.code;
  return {
    ...described,
    message: forbidden ? translate(NO_INBOX) : described.message,
    title:
      HAND_OFF_TITLES[key] ||
      HAND_OFF_TITLES[data.code] ||
      'The report from the Lab Report Inbox was not read'
  };
}

/** A failed "Send to the inbox instead", for beside its button. */
export function describeSendToInboxError(err) {
  const described = describeTestResultError(
    err,
    translate('The report could not be sent to the Lab Report Inbox.')
  );
  const data = err?.response?.data || {};
  return described.status === 403 && !data.code && !data.groupAccess
    ? { ...described, message: translate(NO_INBOX) }
    : described;
}

/**
 * Where a report sent to the inbox went (POST /test-results/inbox/from-intake's answer), as lines.
 * Everything it put on the Test, and everything entered from it, came off again; the Test fields
 * it filled that were changed after it filled them stay as changed (`changed`, by their labels),
 * and are named, to be checked (TGCLOUD-1868, review of the view: this used to say nothing was
 * added while a Test result chosen from it stayed).
 *
 * @param {{ created?: boolean, duplicate?: string }} answer
 * @param {{ attachmentRemoved?: boolean, changed?: string[] }} [options] `changed` is the field
 *   labels, as the reader reads them
 * @param {Function} [t] the caller's translation
 */
export function sentToInboxLines(
  answer,
  { attachmentRemoved = false, changed = [] } = {},
  t = english
) {
  const stays = Array.isArray(changed) ? changed.filter(Boolean) : [];
  const fields = stays.join(', ');
  const staysText = !stays.length
    ? null
    : stays.length === 1
    ? t(
        'What it put on this Test was taken off, except what you changed after it was filled: {{fields}}. Check it before saving the Test.',
        { fields }
      )
    : t(
        'What it put on this Test was taken off, except what you changed after it was filled: {{fields}}. Check them before saving the Test.',
        { fields }
      );
  return [
    answer?.created === false
      ? t(
          'This report was already waiting in the Lab Report Inbox. It stays there until someone matches it to the right patient.'
        )
      : `${t(
          'The report was sent to the Lab Report Inbox, where it waits to be matched to the right patient.'
        )} ${staysText || t('Nothing from it was added to this Test.')}`,
    answer?.created === false ? staysText : null,
    attachmentRemoved
      ? t(
          'It was also taken off this Test’s attachments, and the clinical terms suggested from it are no longer offered.'
        )
      : null
  ].filter(Boolean);
}
